import {format} from 'date-fns';
import {combine, createEvent, merge, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {dateModel} from '@entities/date/model';
import {
    DictionarySearchResponse,
    ViolationDictionaryItem,
    ViolationFilterNode,
    ViolationSearchResponse,
} from '@entities/violations/types';
import {InnerFiltersElement, InnerSortingsElement} from '@shared/types/grid';
import {SelectOption} from '@shared/ui/Select';

import * as api from './api';

const domain = rootDomain.createDomain('AuditViolations');

export const AuditViolationsGate = createGate('AuditViolations');

const fetchViolationsFx = domain.createEffect({handler: api.fetchViolations});
const fetchViolationTypesFx = domain.createEffect({handler: api.fetchViolationTypes});
const fetchViolationStatusesFx = domain.createEffect({handler: api.fetchViolationStatuses});
const fetchNotificationStatusesFx = domain.createEffect({handler: api.fetchNotificationStatuses});

[
    fetchViolationsFx,
    fetchViolationTypesFx,
    fetchViolationStatusesFx,
    fetchNotificationStatusesFx,
].forEach((fx) => fx.failData.watch(showRestError));

const data$ = domain
    .createStore<ViolationSearchResponse | null>(null)
    .on(fetchViolationsFx.doneData, (_, data) => data)
    .reset(AuditViolationsGate.close);

const search$ = domain.createStore('').reset(AuditViolationsGate.close);
const typeIds$ = domain.createStore<string[]>([]).reset(AuditViolationsGate.close);
const violationStatus$ = domain.createStore<string | null>(null).reset(AuditViolationsGate.close);
const notificationStatus$ = domain
    .createStore<string | null>(null)
    .reset(AuditViolationsGate.close);
const sortings$ = domain
    .createStore<Record<string, boolean | undefined>>({})
    .reset(AuditViolationsGate.close);
const pageNumber$ = domain.createStore(0).reset(AuditViolationsGate.close);
const pageSize$ = domain.createStore(10).reset(AuditViolationsGate.close);

export const searchChanged = createEvent<string>();
export const typeIdsChanged = createEvent<string[]>();
export const violationStatusChanged = createEvent<string | null>();
export const notificationStatusChanged = createEvent<string | null>();
export const sortingsChanged = createEvent<InnerSortingsElement[]>();
export const pageNumberChanged = createEvent<number>();
export const pageSizeChanged = createEvent<number>();

search$.on(searchChanged, (_, value) => value);
typeIds$.on(typeIdsChanged, (_, ids) => ids);
violationStatus$.on(violationStatusChanged, (_, status) => status);
notificationStatus$.on(notificationStatusChanged, (_, status) => status);
sortings$.on(sortingsChanged, (state, sortings) => ({
    ...state,
    ...Object.fromEntries(sortings.map(({id, value}) => [id, value])),
}));
pageNumber$.on(pageNumberChanged, (_, page) => page);
pageSize$.on(pageSizeChanged, (_, size) => size);

pageNumber$.reset(
    searchChanged,
    typeIdsChanged,
    violationStatusChanged,
    notificationStatusChanged,
    pageSizeChanged,
);

const violationTypes$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(fetchViolationTypesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(AuditViolationsGate.close);

const violationStatuses$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(fetchViolationStatusesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(AuditViolationsGate.close);

const notificationStatuses$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(
        fetchNotificationStatusesFx.doneData,
        (_, data: DictionarySearchResponse) => data.data ?? [],
    )
    .reset(AuditViolationsGate.close);

const buildFilter = ({
    from,
    to,
    search,
    typeIds,
    violationStatus,
    notificationStatus,
}: {
    from: Date | null;
    to: Date | null;
    search: string;
    typeIds: string[];
    violationStatus: string | null;
    notificationStatus: string | null;
}): ViolationFilterNode => {
    const children: ViolationFilterNode[] = [{type: 'ISNULL', field: 'validTo', values: []}];

    const fromPeriod = from ? format(from, 'yyyyMM') : null;
    const toPeriod = to ? format(to, 'yyyyMM') : null;

    if (fromPeriod && toPeriod) {
        children.push(
            fromPeriod === toPeriod
                ? {type: 'EQ', field: 'taxPeriod', values: [fromPeriod]}
                : {type: 'BTW', field: 'taxPeriod', values: [fromPeriod, toPeriod]},
        );
    }

    const trimmedSearch = search.trim();
    if (trimmedSearch) {
        children.push({
            type: 'OR',
            children: [
                {type: 'ILIKE', field: 'payerTin', values: [`%${trimmedSearch}%`]},
                {type: 'ILIKE', field: 'payerName', values: [`%${trimmedSearch}%`]},
            ],
        });
    }

    if (typeIds.length) children.push({type: 'IN', field: 'type', values: typeIds});
    if (violationStatus)
        children.push({type: 'EQ', field: 'status.code', values: [violationStatus]});
    if (notificationStatus)
        children.push({type: 'EQ', field: 'notificationStatus.code', values: [notificationStatus]});

    return {type: 'AND', children};
};

const mapSorts = (sortings: Record<string, boolean | undefined>) => {
    const [field, value] = Object.entries(sortings)[0] ?? [];
    if (!field || value == null) return null;
    return [{field, order: value ? 'DESC' : 'ASC'} as const];
};

const toSelectedIds = (value: readonly SelectOption<string>[]) =>
    value.map((item) => item.value).filter((item): item is string => Boolean(item));

export const filtersChanged = createEvent<InnerFiltersElement[]>();
filtersChanged.watch((filters) => {
    const {id, value} = filters[0] ?? {};
    if (id === 'source') typeIdsChanged(toSelectedIds(value as readonly SelectOption<string>[]));
    if (id === 'violationStatus')
        violationStatusChanged(
            ((value as SelectOption<string> | null)?.value ?? null) as string | null,
        );
    if (id === 'sendingStatus')
        notificationStatusChanged(
            ((value as SelectOption<string> | null)?.value ?? null) as string | null,
        );
});

export const filtersReset = createEvent<string[]>();
filtersReset.watch((ids) => {
    const id = ids[0];
    if (id === 'source') typeIdsChanged([]);
    if (id === 'violationStatus') violationStatusChanged(null);
    if (id === 'sendingStatus') notificationStatusChanged(null);
});

sample({
    clock: AuditViolationsGate.open,
    target: [fetchViolationTypesFx, fetchViolationStatusesFx, fetchNotificationStatusesFx],
});

sample({
    clock: merge([
        AuditViolationsGate.open,
        dateModel.changed,
        searchChanged,
        typeIdsChanged,
        violationStatusChanged,
        notificationStatusChanged,
        sortingsChanged,
        pageNumberChanged,
        pageSizeChanged,
    ]),
    source: combine({
        date: dateModel.state,
        search: search$,
        typeIds: typeIds$,
        violationStatus: violationStatus$,
        notificationStatus: notificationStatus$,
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
    }),
    filter: AuditViolationsGate.status,
    fn: ({
        date,
        search,
        typeIds,
        violationStatus,
        notificationStatus,
        sortings,
        pageNumber,
        pageSize,
    }) => ({
        filter: buildFilter({
            from: date.range.from,
            to: date.range.to,
            search,
            typeIds,
            violationStatus,
            notificationStatus,
        }),
        sorts: mapSorts(sortings),
        page: {page: pageNumber, size: pageSize},
    }),
    target: fetchViolationsFx,
});

const toOption = (item: ViolationDictionaryItem): SelectOption<string> => ({
    value: item.id,
    label: item.title?.en ?? Object.values(item.title ?? {})[0] ?? item.code,
});

const toCodeOption = (item: ViolationDictionaryItem): SelectOption<string> => ({
    value: item.code,
    label: item.title?.en ?? Object.values(item.title ?? {})[0] ?? item.code,
});

const filters$ = combine(
    {
        typeIds: typeIds$,
        violationStatus: violationStatus$,
        notificationStatus: notificationStatus$,
        violationTypes: violationTypes$,
        violationStatuses: violationStatuses$,
        notificationStatuses: notificationStatuses$,
    },
    ({
        typeIds,
        violationStatus,
        notificationStatus,
        violationTypes,
        violationStatuses,
        notificationStatuses,
    }) => ({
        source: violationTypes.filter((item) => typeIds.includes(item.id)).map(toOption),
        violationStatus: violationStatuses.find((item) => item.code === violationStatus)
            ? toCodeOption(violationStatuses.find((item) => item.code === violationStatus)!)
            : undefined,
        sendingStatus: notificationStatuses.find((item) => item.code === notificationStatus)
            ? toCodeOption(notificationStatuses.find((item) => item.code === notificationStatus)!)
            : undefined,
    }),
);

const pending$ = combine(
    [
        fetchViolationsFx.pending,
        fetchViolationTypesFx.pending,
        fetchViolationStatusesFx.pending,
        fetchNotificationStatusesFx.pending,
    ],
    (list) => list.some(Boolean),
);

const $auditViolationsState = combine({
    data: data$,
    search: search$,
    filters: filters$,
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    violationTypes: violationTypes$,
    violationStatuses: violationStatuses$,
    notificationStatuses: notificationStatuses$,
    pending: pending$,
});

export const auditViolationsModel = {
    state: $auditViolationsState,
    searchChanged,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    pageNumberChanged,
    pageSizeChanged,
};
